<template>
  <div class="dwg" :class="{ 'dwg--mobile': isMobileLayout }">
    <div class="dwg-toolbar">
      <DashboardPresetBar
        :presets="presets"
        :active-id="activePresetId"
        :dirty="presetDirty"
        @select="selectPreset"
        @edit="editPreset"
        @create="createPreset"
        @save-changes="saveActivePreset"
      />
      <span v-if="saveState === 'saving'" class="dwg-toolbar__state">{{ $tn("dashboardWidgets.saving") }}</span>
      <span v-else-if="saveState === 'error'" class="dwg-toolbar__state text-danger">{{ $tn("dashboardWidgets.saveError") }}</span>
      <button
        v-if="isMobileLayout && visibleWidgets.length > 1"
        type="button"
        data-tour="dw-reorder"
        class="btn btn-sm"
        :class="reordering ? 'btn-primary' : 'btn-outline-secondary'"
        :title="reordering ? $tn('dashboardWidgets.doneReordering') : $tn('dashboardWidgets.reorder')"
        @click="reordering = !reordering"
      >
        <i class="fas" :class="reordering ? 'fa-check' : 'fa-sort'" aria-hidden="true"></i>
        <span class="dwg-toolbar__label">{{ reordering ? $tn("dashboardWidgets.doneReordering") : $tn("dashboardWidgets.reorder") }}</span>
      </button>
      <!-- Botones del dashboard que no son de un widget (p. ej. notificaciones). -->
      <slot name="toolbar" />
      <DashboardTourLauncher :ready="layoutReady && sourcesLoaded" />
      <button
        type="button"
        class="btn btn-sm btn-outline-primary dwg-toolbar__add"
        data-tour="dw-add"
        :title="$tn('dashboardWidgets.manage')"
        @click="openCatalog"
      >
        <i class="fas fa-th-large" aria-hidden="true"></i>
        <span class="dwg-toolbar__label">{{ $tn("dashboardWidgets.manage") }}</span>
      </button>
    </div>

    <p v-if="layoutReady && !visibleWidgets.length" class="dwg-empty">
      {{ $tn("dashboardWidgets.emptyDashboard") }}
      <button type="button" class="btn btn-sm btn-primary ms-2" @click="openCatalog">
        <i class="fas fa-plus" aria-hidden="true"></i> {{ $tn("dashboardWidgets.catalog.add") }}
      </button>
    </p>

    <!-- Escritorio: grilla de 12 columnas; se arrastra desde el encabezado y se cambia
         el tamaño desde la esquina. -->
    <!-- Al tomar un widget la grilla entera se achica hasta entrar en la pantalla,
         anclada en el puntero: así se lo puede llevar arriba de todo sin scrollear. -->
    <div
      v-if="!isMobileLayout && layoutReady"
      ref="canvas"
      class="dwg-canvas"
      :class="{ 'is-zoomed': dragScale < 1 }"
      :style="canvasStyle"
      @pointerdown.capture="maybeZoomOut"
    >
    <grid-layout
      ref="grid"
      :transform-scale="dragScale"
      :layout="visibleWidgets"
      :col-num="cols"
      :row-height="rowHeight"
      :margin="[margin, margin]"
      :is-draggable="true"
      :is-resizable="true"
      :vertical-compact="true"
      :use-css-transforms="false"
      :responsive="false"
    >
      <grid-item
        v-for="w in visibleWidgets"
        :key="w.i"
        :i="w.i"
        :x="w.x"
        :y="w.y"
        :w="w.w"
        :h="w.h"
        :min-w="entryOf(w).min.w"
        :min-h="entryOf(w).min.h"
        drag-allow-from=".dw-head"
        :drag-option="dragOption"
        drag-ignore-from="button, a, input, textarea, select, .dw-actions"
        :class="{ 'dwg-item--menu-open': openMenuId === w.i }"
        @moved="scheduleSave"
        @resized="scheduleSave"
      >
        <DashboardWidgetFrame
          :title="titleOf(w)"
          :icon="entryOf(w).icon"
          :menu-items="menus[w.i] || []"
          :refreshable="isRefreshable(w)"
          :flush="entryOf(w).kind === 'builtin'"
          draggable
          @menu="onMenu(w, $event)"
          @menu-open="openMenuId = $event ? w.i : null"
          @remove="removeWidget(w)"
        >
          <template v-if="hasActionsSlot(w)" #actions>
            <slot :name="`widget-actions-${w.type}`" />
          </template>
          <div v-if="entryOf(w).kind === 'builtin'" :key="`${w.i}-${refreshKeys[w.i] || 0}`" class="dwg-builtin">
            <slot :name="`widget-${w.type}`" />
          </div>
          <component
            :is="componentOf(w)"
            v-else
            :key="`${w.i}-${refreshKeys[w.i] || 0}`"
            :ref="`widget-${w.i}`"
            :entry="entryOf(w)"
            :config="w.config || {}"
            @menu="setMenu(w, $event)"
            @update-config="updateConfig(w, $event)"
          />
        </DashboardWidgetFrame>
      </grid-item>
    </grid-layout>
    </div>

    <!-- Teléfono: una columna en el orden del layout, cada widget con su alto natural. -->
    <div v-else-if="isMobileLayout && layoutReady" class="dwg-stack">
      <DashboardWidgetFrame
        v-for="(w, idx) in mobileOrder"
        :key="w.i"
        mobile
        :class="{ 'dw--capped': entryOf(w).kind !== 'builtin' }"
        :title="titleOf(w)"
        :icon="entryOf(w).icon"
        :menu-items="menus[w.i] || []"
        :refreshable="isRefreshable(w)"
        :flush="entryOf(w).kind === 'builtin'"
        :reordering="reordering"
        :can-up="idx > 0"
        :can-down="idx < mobileOrder.length - 1"
        @menu="onMenu(w, $event)"
        @remove="removeWidget(w)"
        @up="moveMobile(idx, -1)"
        @down="moveMobile(idx, 1)"
      >
        <template v-if="hasActionsSlot(w) && !reordering" #actions>
          <slot :name="`widget-actions-${w.type}`" />
        </template>
        <div v-if="entryOf(w).kind === 'builtin'" :key="`${w.i}-${refreshKeys[w.i] || 0}`" class="dwg-builtin">
          <slot :name="`widget-${w.type}`" />
        </div>
        <component
          :is="componentOf(w)"
          v-else
          :key="`${w.i}-${refreshKeys[w.i] || 0}`"
          :ref="`widget-${w.i}`"
          :entry="entryOf(w)"
          :config="w.config || {}"
          @menu="setMenu(w, $event)"
          @update-config="updateConfig(w, $event)"
        />
      </DashboardWidgetFrame>
    </div>

    <DashboardPresetDialog
      v-model="presetDialogOpen"
      :preset="presetEditing"
      :suggested-name="presetSuggestedName"
      :current-widgets="widgets"
      :is-admin="ctx.isAdmin"
      @created="onPresetCreated"
      @saved="loadPresets"
      @changed="loadPresets"
      @overwritten="onPresetOverwritten"
      @deleted="onPresetDeleted"
      @applied-to-all="onAppliedToAll"
    />

    <DashboardWidgetCatalog
      v-model="catalogOpen"
      :entries="catalog"
      :added-types="addedTypes"
      :loading="!sourcesLoaded"
      @add="addWidget"
      @remove="removeByType"
      @reset="resetLayout"
    />
  </div>
</template>

<script>
/**
 * Dashboard por widgets: cada usuario elige cuáles ve, los mueve y les cambia el
 * tamaño (se guarda en UserDashboardLayout). Los widgets disponibles salen de los
 * módulos del tenant y de los permisos del usuario (widgetCatalog.js).
 *
 * Los bloques de siempre (calendario, actividad reciente, resumen, accesos) los
 * dibuja Dashboard.vue por slot `widget-<type>`, con sus datos de siempre; el resto
 * son componentes propios (widgets/).
 */
import { GridLayout, GridItem } from "vue-grid-layout";
import Vue from "vue";
import Swal from "sweetalert2";
import { wencesApi } from "@/api/wencesApi";
import mobileLayoutMixin from "@/mixins/mobileLayoutMixin";
import gridDragMixin from "./gridDragMixin";
import "./widgetTokens.css";
import DashboardWidgetFrame from "./DashboardWidgetFrame.vue";
import DashboardWidgetCatalog from "./DashboardWidgetCatalog.vue";
import DashboardPresetBar from "./DashboardPresetBar.vue";
import DashboardPresetDialog from "./DashboardPresetDialog.vue";
import DashboardTourLauncher from "./DashboardTourLauncher.vue";
import ReportWidget from "./widgets/ReportWidget.vue";
import ContainersWidget from "./widgets/ContainersWidget.vue";
import StatsWidget from "./widgets/StatsWidget.vue";
import NoteWidget from "./widgets/NoteWidget.vue";
import TrackingWidget from "./widgets/TrackingWidget.vue";
import NotificationsWidget from "./widgets/NotificationsWidget.vue";
import FinanceWidget from "./widgets/FinanceWidget.vue";
import TradeWidget from "./widgets/TradeWidget.vue";
import ClientQuestionsWidget from "./widgets/ClientQuestionsWidget.vue";
import {
  GRID_COLS,
  ROW_HEIGHT,
  GRID_MARGIN,
  buildCatalog,
  buildDefaultLayout,
  placeNewWidget,
  newWidgetId,
} from "./widgetCatalog";
import { fetchDashboardLayout, saveDashboardLayout, resetDashboardLayout, fetchDashboardPresets } from "./dashboardLayoutApi";

const COMPONENTS = {
  report: ReportWidget,
  containers: ContainersWidget,
  "stock-health": StatsWidget,
  support: StatsWidget,
  note: NoteWidget,
  tracking: TrackingWidget,
  notifications: NotificationsWidget,
  finance: FinanceWidget,
  trade: TradeWidget,
  "client-questions": ClientQuestionsWidget,
};

export default {
  name: "DashboardWidgetGrid",
  components: { GridLayout, GridItem, DashboardWidgetFrame, DashboardWidgetCatalog, DashboardPresetBar, DashboardPresetDialog, DashboardTourLauncher },
  mixins: [mobileLayoutMixin, gridDragMixin],
  inject: {
    dashboardLayout: { from: "dashboardLayout", default: null },
  },
  props: {
    isDuntower: { type: Boolean, default: false },
    showLegalShortcuts: { type: Boolean, default: false },
  },
  data() {
    return {
      cols: GRID_COLS,
      rowHeight: ROW_HEIGHT,
      margin: GRID_MARGIN,
      /** null = nunca lo personalizó: se muestra el de fábrica, que sigue a los permisos. */
      savedWidgets: null,
      layoutReady: false,
      reports: [],
      queryViews: [],
      sourcesLoaded: false,
      catalogOpen: false,
      presets: [],
      /** Dashboard guardado en uso (botón marcado) y si se cambió después de elegirlo. */
      activePresetId: null,
      presetDirty: false,
      presetDialogOpen: false,
      presetEditing: null,
      presetSuggestedName: "",
      /** Dashboard ajeno por el que ya se avisó (para no preguntar con cada cambio). */
      foreignWarnedId: null,
      reordering: false,
      menus: {},
      refreshKeys: {},
      openMenuId: null,
      saveState: "",
      /** El tenant tiene dudas para el cliente cargadas (widget «Dudas»). */
      hasClientQuestions: false,
    };
  },
  computed: {
    ctx() {
      const d = this.dashboardLayout;
      let role = "";
      try {
        role = localStorage.getItem("role") || "";
      } catch (e) {
        role = "";
      }
      const isAdmin = d && typeof d.isAdminRole === "boolean" ? d.isAdminRole : role === "admin";
      return {
        isAdmin,
        // Leer `userPermissions` acá hace que el catálogo se recalcule cuando llegan.
        permissions: d ? d.userPermissions : [],
        hasPermission: (p) => isAdmin || !!(d && typeof d.hasPermission === "function" && d.hasPermission(p)),
        canAccessStock: !!(d && d.canAccessStockModule),
        canAccessSupport: !!(d && d.canAccessSupportModule),
        canAccessTracking: !!(d && d.canAccessFindTeuModule),
        // Mismos permisos que el menú de finanzas: dashboard = finance.manage; los
        // pedidos de pago también con finance.payments.
        canAccessFinance: !!(d && d.canAccessFinancialDashboard),
        canAccessFinancePayments: !!(d && d.canAccessFinancialPayments),
        showLegalShortcuts: this.showLegalShortcuts,
        hasClientQuestions: this.hasClientQuestions,
      };
    },
    catalog() {
      return buildCatalog({ ctx: this.ctx, reports: this.reports, queryViews: this.queryViews });
    },
    catalogByType() {
      const map = {};
      this.catalog.forEach((e) => {
        map[e.type] = e;
      });
      return map;
    },
    defaultWidgets() {
      // Observable: el ajuste de ancho al arrastrar cambia `w` en el lugar y la
      // grilla tiene que enterarse aunque el layout todavía sea el de fábrica.
      return Vue.observable(buildDefaultLayout({ catalog: this.catalog, isDuntower: this.isDuntower }));
    },
    widgets() {
      return this.savedWidgets || this.defaultWidgets;
    },
    /** Los que el usuario puede ver hoy; un reporte sin permiso queda guardado pero oculto. */
    visibleWidgets() {
      return this.widgets.filter((w) => this.catalogByType[w.type]);
    },
    mobileOrder() {
      return [...this.visibleWidgets].sort((a, b) => a.y - b.y || a.x - b.x);
    },
    addedTypes() {
      return this.visibleWidgets.map((w) => w.type);
    },
  },
  async mounted() {
    this.loadSources();
    this.loadPresets();
    try {
      this.applyLayoutState(await fetchDashboardLayout());
    } catch (e) {
      this.savedWidgets = null;
    } finally {
      this.layoutReady = true;
    }
    this.loadClientQuestions();
  },
  beforeDestroy() {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.persist();
    }
  },
  methods: {
    async loadSources() {
      try {
        const [reportsRes, viewsRes] = await Promise.all([
          wencesApi.get("/api/gotrading/reports").catch(() => ({ data: [] })),
          this.ctx.isAdmin ? wencesApi.get("/api/gotrading/views").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        ]);
        this.reports = Array.isArray(reportsRes.data) ? reportsRes.data : [];
        this.queryViews = (Array.isArray(viewsRes.data) ? viewsRes.data : []).filter((v) => v && v.type === "query_report");
      } finally {
        this.sourcesLoaded = true;
      }
    },
    entryOf(w) {
      return this.catalogByType[w.type] || { min: { w: 2, h: 3 }, icon: "", kind: "" };
    },
    componentOf(w) {
      return COMPONENTS[this.entryOf(w).kind] || null;
    },
    titleOf(w) {
      const e = this.entryOf(w);
      return e.title || this.$t(`dashboardWidgets.items.${w.type.replace(/[:-]/g, "_")}.title`);
    },
    hasActionsSlot(w) {
      return !!this.$scopedSlots[`widget-actions-${w.type}`];
    },
    /** El calendario y el resumen viven de los datos de Dashboard.vue: remontarlos no los recarga. */
    isRefreshable(w) {
      const e = this.entryOf(w);
      return e.kind !== "builtin" || w.type === "recent-activity";
    },
    setMenu(w, items) {
      this.$set(this.menus, w.i, items || []);
    },
    onMenu(w, key) {
      const ref = this.$refs[`widget-${w.i}`];
      const vm = Array.isArray(ref) ? ref[0] : ref;
      if (key === "refresh") {
        if (vm && typeof vm.beforeRefresh === "function") vm.beforeRefresh();
        this.$set(this.refreshKeys, w.i, (this.refreshKeys[w.i] || 0) + 1);
        return;
      }
      if (vm && typeof vm.onMenu === "function") {
        vm.onMenu(key);
        return;
      }
      // En el teléfono no hay ref (se dibuja otra lista): las opciones comunes se
      // resuelven acá.
      this.handleMenuWithoutRef(w, key);
    },
    handleMenuWithoutRef(w, key) {
      const e = this.entryOf(w);
      if (key.startsWith("mode:")) this.updateConfig(w, { mode: key.slice(5) });
      else if (key.startsWith("group:")) this.updateConfig(w, { groupField: key.slice(6) });
      else if (key.startsWith("year:")) this.updateConfig(w, { year: key.slice(5) });
      else if (key.startsWith("basis:")) this.updateConfig(w, { basis: key.slice(6) });
      else if (key.startsWith("currency:")) this.updateConfig(w, { currency: key.slice(9) });
      else if (key === "open") {
        if (e.kind === "report") {
          const c = w.config || {};
          if (c.reportId && e.modelName) this.$router.push(`/dynamic-model/${e.modelName}?reportId=${c.reportId}`);
          else if (c.viewId) this.$router.push(`/view/${c.viewId}`);
        } else if (e.kind === "containers") this.$router.push("/containers");
        else if (e.kind === "support") this.$router.push("/support/tickets");
        else if (e.kind === "stock-health") this.$router.push("/stock");
        else if (e.kind === "tracking") this.$router.push("/ft/container-tracking");
        else if (e.kind === "finance") this.$router.push("/financial");
      }
    },
    /**
     * El widget «Dudas» existe solo en los tenants con dudas cargadas. Si hay pendientes y
     * el usuario no lo tiene, se pone arriba a la izquierda (el resto baja). Si lo sacó,
     * vuelve solo cuando hay una duda pendiente más nueva que ese momento.
     */
    async loadClientQuestions() {
      let s;
      try {
        const { data } = await wencesApi.get("/api/client-questions/summary", { params: { _: Date.now() } });
        s = (data && data.data) || {};
      } catch (e) {
        return;
      }
      this.hasClientQuestions = s.total > 0;
      const entry = this.catalogByType["client-questions"];
      if (!entry || this.visibleWidgets.some((w) => w.type === "client-questions")) return;
      if (!s.pending || !s.latestPendingAt) return;
      if (s.widgetDismissedAt && new Date(s.latestPendingAt) <= new Date(s.widgetDismissedAt)) return;
      this.materialize();
      const { w, h } = entry.size;
      this.savedWidgets = this.savedWidgets.filter((x) => x.i !== entry.type);
      this.savedWidgets.forEach((x) => {
        if (x.x < w) x.y += h;
      });
      this.savedWidgets.unshift({ i: entry.type, type: entry.type, x: 0, y: 0, w, h, config: {} });
      this.scheduleSave(0, { fromUser: false });
    },
    /** Primera modificación: el de fábrica pasa a ser el layout propio del usuario. */
    materialize() {
      if (!this.savedWidgets) {
        this.savedWidgets = this.defaultWidgets.map((w) => ({ ...w, config: { ...(w.config || {}) } }));
      }
    },
    updateConfig(w, patch) {
      this.materialize();
      const target = this.savedWidgets.find((x) => x.i === w.i);
      if (!target) return;
      target.config = { ...(target.config || {}), ...patch };
      this.scheduleSave();
    },
    addWidget(entry) {
      this.materialize();
      const { w, h } = entry.size;
      const pos = this.isMobileLayout
        ? { x: 0, y: this.savedWidgets.reduce((m, it) => Math.max(m, it.y + it.h), 0) }
        : placeNewWidget(this.visibleWidgets, w);
      const id = entry.multiple ? newWidgetId(entry.type) : entry.type;
      // Si ya estaba guardado pero oculto (p. ej. sin permiso un tiempo), se reemplaza.
      this.savedWidgets = this.savedWidgets.filter((x) => x.i !== id);
      this.savedWidgets.push({ i: id, type: entry.type, x: pos.x, y: pos.y, w, h, config: { ...(entry.config || {}) } });
      this.scheduleSave(0);
    },
    removeWidget(w) {
      // Sacar «Dudas» a mano: no vuelve a aparecer solo hasta que llegue una duda nueva.
      if (w.type === "client-questions") wencesApi.post("/api/client-questions/widget-dismissed").catch(() => {});
      this.materialize();
      this.savedWidgets = this.savedWidgets.filter((x) => x.i !== w.i);
      this.$delete(this.menus, w.i);
      this.scheduleSave(0);
    },
    removeByType(entry) {
      const w = this.visibleWidgets.find((x) => x.type === entry.type);
      if (w) this.removeWidget(w);
    },
    /** En el teléfono se intercambia la posición con el vecino; el escritorio se reacomoda solo. */
    moveMobile(idx, dir) {
      this.materialize();
      const order = this.mobileOrder;
      const a = this.savedWidgets.find((x) => x.i === order[idx].i);
      const b = this.savedWidgets.find((x) => x.i === order[idx + dir].i);
      if (!a || !b) return;
      const ax = a.x;
      const ay = a.y;
      a.x = b.x;
      a.y = b.y;
      b.x = ax;
      b.y = ay;
      if (a.y === b.y && a.x === b.x) b.y += dir > 0 ? -1 : 1;
      this.scheduleSave();
    },
    applyLayoutState(state) {
      const widgets = state && state.widgets;
      this.menus = {};
      this.savedWidgets = Array.isArray(widgets) ? widgets.map((w) => ({ config: {}, ...w })) : null;
      this.activePresetId = (state && state.activePresetId) || null;
      this.presetDirty = !!(state && state.presetDirty);
    },
    async loadPresets() {
      try {
        this.presets = await fetchDashboardPresets();
      } catch (e) {
        this.presets = [];
      }
      // Si el que estaba en uso ya no existe (lo borraron), deja de estar marcado.
      if (this.activePresetId && !this.presets.some((p) => p._id === this.activePresetId)) {
        this.activePresetId = null;
        this.presetDirty = false;
      }
    },
    /** Click en el botón de un dashboard guardado: se usa ese (se copia al layout propio). */
    async selectPreset(p) {
      if (p._id === this.activePresetId && !this.presetDirty) return;
      // El que está en uso, con cambios: volver a como estaba guardado.
      if (p._id === this.activePresetId) {
        const { isConfirmed } = await Swal.fire({
          title: this.$tn("dashboardWidgets.presets.discardTitle", { name: p.name }),
          text: this.$tn("dashboardWidgets.presets.discardText"),
          icon: "question",
          showCancelButton: true,
          confirmButtonText: this.$tn("dashboardWidgets.presets.discard"),
          cancelButtonText: this.$tn("dashboardWidgets.presets.cancel"),
        });
        if (!isConfirmed) return;
        this.useWidgetsOf(p);
        return;
      }
      const losing = this.presetDirty || (!this.activePresetId && this.savedWidgets);
      if (losing) {
        const current = this.presets.find((x) => x._id === this.activePresetId);
        const { isConfirmed } = await Swal.fire({
          title: this.$tn("dashboardWidgets.presets.switchTitle", { name: p.name }),
          text: current
            ? this.$tn("dashboardWidgets.presets.switchTextDirty", { name: current.name })
            : this.$tn("dashboardWidgets.presets.switchTextUnsaved"),
          icon: "question",
          showCancelButton: true,
          confirmButtonText: this.$tn("dashboardWidgets.presets.switchConfirm"),
          cancelButtonText: this.$tn("dashboardWidgets.presets.cancel"),
        });
        if (!isConfirmed) return;
      }
      this.useWidgetsOf(p);
    },
    useWidgetsOf(p) {
      this.foreignWarnedId = null;
      clearTimeout(this.saveTimer);
      this.menus = {};
      this.savedWidgets = (p.widgets || []).map((w) => ({ ...w, config: { ...(w.config || {}) } }));
      this.activePresetId = p._id;
      this.presetDirty = false;
      this.scheduleSave(0, { fromUser: false });
    },
    /**
     * 💾 junto al dashboard en uso: guarda en él cómo está ahora. Si es de otro (y no se
     * puede editar), abre «Guardar como nuevo».
     */
    async saveActivePreset(p) {
      if (!p.canEdit) {
        this.createPreset(this.$tn("dashboardWidgets.presets.copyName", { name: p.name }));
        return;
      }
      const widgets = this.widgets.map(({ i, type, x, y, w, h, config }) => ({ i, type, x, y, w, h, config: config || {} }));
      try {
        await wencesApi.put(`/api/users/dashboards/${p._id}`, { widgets });
        await this.loadPresets();
        // Sin punto amarillo = guardado.
        this.markPresetInUse(p._id);
      } catch (e) {
        Swal.fire({ icon: "error", title: this.$tn("dashboardWidgets.presets.saveError") });
      }
    },
    editPreset(p) {
      this.presetEditing = p;
      this.presetDialogOpen = true;
    },
    createPreset(suggestedName = "") {
      this.presetEditing = null;
      this.presetSuggestedName = typeof suggestedName === "string" ? suggestedName : "";
      this.presetDialogOpen = true;
    },
    /**
     * Primer cambio sobre un dashboard que no es del usuario (ni puede editarlo): no se
     * puede pisar, así que se avisa y se propone guardarlo como uno nuevo. Una vez por
     * dashboard elegido.
     */
    async warnForeignPreset() {
      const p = this.presets.find((x) => x._id === this.activePresetId);
      if (!p || p.canEdit || this.foreignWarnedId === p._id) return;
      this.foreignWarnedId = p._id;
      const owner = (p.createdBy && p.createdBy.name) || "";
      const { isConfirmed } = await Swal.fire({
        title: this.$tn("dashboardWidgets.presets.foreignTitle"),
        text: owner
          ? this.$tn("dashboardWidgets.presets.foreignText", { name: p.name, owner })
          : this.$tn("dashboardWidgets.presets.foreignTextNoOwner", { name: p.name }),
        icon: "info",
        showCancelButton: true,
        confirmButtonText: this.$tn("dashboardWidgets.presets.saveAsNewShort"),
        cancelButtonText: this.$tn("dashboardWidgets.presets.keepWithoutSaving"),
      });
      if (isConfirmed) this.createPreset(this.$tn("dashboardWidgets.presets.copyName", { name: p.name }));
    },
    /** El dashboard actual quedó guardado con ese nombre: pasa a ser el que está en uso. */
    async onPresetCreated(p) {
      await this.loadPresets();
      if (p && p._id) this.markPresetInUse(p._id);
    },
    async onPresetOverwritten(p) {
      await this.loadPresets();
      if (p && p._id) this.markPresetInUse(p._id);
    },
    async onPresetDeleted(id) {
      if (id === this.activePresetId) {
        this.activePresetId = null;
        this.presetDirty = false;
        this.scheduleSave(0, { fromUser: false });
      }
      await this.loadPresets();
    },
    /** El admin lo aplicó a todos: el propio también cambió en el back. */
    async onAppliedToAll() {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      this.applyLayoutState(await fetchDashboardLayout({ fresh: true }));
      await this.loadPresets();
    },
    markPresetInUse(id) {
      this.activePresetId = id;
      this.presetDirty = false;
      this.scheduleSave(0, { fromUser: false });
    },
    openCatalog() {
      this.catalogOpen = true;
    },
    async resetLayout() {
      const { isConfirmed } = await Swal.fire({
        title: this.$tn("dashboardWidgets.resetConfirmTitle"),
        text: this.$tn("dashboardWidgets.resetConfirmText"),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$tn("dashboardWidgets.catalog.reset"),
        cancelButtonText: this.$tn("dashboardWidgets.cancel"),
      });
      if (!isConfirmed) return;
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      try {
        await resetDashboardLayout();
        // Sin layout propio el back devuelve el predeterminado del admin, si hay.
        this.applyLayoutState(await fetchDashboardLayout({ fresh: true }));
        this.catalogOpen = false;
      } catch (e) {
        this.saveState = "error";
      }
    },
    /** `fromUser`: un cambio del usuario sobre un dashboard guardado lo deja «con cambios». */
    scheduleSave(delay = 800, { fromUser = true } = {}) {
      this.materialize();
      if (fromUser && this.activePresetId) {
        this.presetDirty = true;
        this.warnForeignPreset();
      }
      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => {
        this.saveTimer = null;
        this.persist();
      }, delay);
    },
    /**
     * Los guardados van en fila: si dos se pisan, el último en salir es el último en
     * llegar (si no, uno viejo podía terminar después y dejar el layout anterior).
     */
    persist() {
      this.persistChain = (this.persistChain || Promise.resolve()).then(() => this.persistNow());
      return this.persistChain;
    },
    async persistNow() {
      if (!this.savedWidgets) return;
      const payload = this.savedWidgets.map(({ i, type, x, y, w, h, config }) => ({ i, type, x, y, w, h, config: config || {} }));
      this.saveState = "saving";
      try {
        await saveDashboardLayout(payload, { activePresetId: this.activePresetId, presetDirty: this.presetDirty });
        this.saveState = "";
      } catch (e) {
        this.saveState = "error";
      }
    },
  },
};
</script>

<style>
/* Durante el tutorial los tiradores de tamaño se ven siempre (el recorrido señala uno). */
body.gt-running .dwg .vue-grid-item > .vue-resizable-handle { opacity: 1 !important; }
</style>

<style scoped>
.dwg { position: relative; }
.dwg-toolbar {
  display: flex;
  justify-content: flex-end;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  margin: 10px 0 4px;
  padding: 0 12px;
}
.dwg-toolbar__state { font-size: 0.78rem; color: var(--dw-muted); }
.dwg-toolbar .btn { display: inline-flex; align-items: center; gap: 6px; }
@media (max-width: 767.98px) {
  .dwg-toolbar__label { display: none; }
}
.dwg-empty {
  text-align: center;
  padding: 40px 12px;
  color: var(--dw-muted);
  border: 2px dashed var(--dw-border);
  border-radius: 12px;
  margin: 8px 12px;
}
.dwg ::v-deep .vue-grid-item.vue-grid-placeholder {
  background: rgba(var(--bs-primary-rgb), 0.18);
  border-radius: 12px;
  opacity: 1;
}
.dwg ::v-deep .vue-grid-item.resizing,
.dwg ::v-deep .vue-grid-item.vue-draggable-dragging { z-index: 30; opacity: 0.92; }
.dwg ::v-deep .vue-grid-item > .vue-resizable-handle {
  background: none;
  width: 16px;
  height: 16px;
  right: 3px;
  bottom: 3px;
  opacity: 0;
  transition: opacity 0.15s ease;
  z-index: 5;
}
.dwg ::v-deep .vue-grid-item > .vue-resizable-handle::after {
  content: "";
  position: absolute;
  right: 3px;
  bottom: 3px;
  width: 8px;
  height: 8px;
  border-right: 2px solid var(--dw-muted);
  border-bottom: 2px solid var(--dw-muted);
  border-bottom-right-radius: 3px;
}
.dwg ::v-deep .vue-grid-item:hover > .vue-resizable-handle { opacity: 1; }
.dwg ::v-deep .vue-grid-item.dwg-item--menu-open { z-index: 25; }
.dwg-builtin { height: 100%; }
/* Achicar es instantáneo (la grilla mide al empezar el arrastre); volver, suave. */
.dwg-canvas { transition: transform 0.2s ease; }
.dwg-canvas.is-zoomed { transition: none; }
.dwg-canvas.is-zoomed ::v-deep .vue-grid-item:not(.vue-draggable-dragging):not(.vue-grid-placeholder) { opacity: 0.85; }
.dwg-stack { display: flex; flex-direction: column; gap: 12px; padding: 0 8px 12px; }
.dwg--mobile .dwg-toolbar { padding: 0 8px; margin-bottom: 8px; flex-wrap: wrap; row-gap: 8px; gap: 6px; }
/* Teléfono: los botones de la barra, todos cuadrados del mismo tamaño y solo
   con ícono (el texto queda en el title); con texto, borde y alto distintos
   se veían desordenados. */
.dwg--mobile .dwg-toolbar__label { display: none; }
/* Sin tocar los modales que viven dentro de la barra (la campanita). */
.dwg--mobile .dwg-toolbar > .btn,
.dwg--mobile .dwg-toolbar ::v-deep .btn:not(.modal .btn),
.dwg--mobile .dwg-toolbar ::v-deep .dtl__start,
.dwg--mobile .dwg-toolbar ::v-deep .dtl__close {
  width: 38px;
  height: 38px;
  margin: 0;
  padding: 0;
  justify-content: center;
  border-radius: 10px;
}
.dwg--mobile .dwg-toolbar ::v-deep .material-icons:not(.modal .material-icons) { font-size: 18px; }
.dwg--mobile .dwg-toolbar ::v-deep .dtl { height: 38px; border-radius: 10px; }
/* La «x» del tutorial es secundaria: más angosta que el botón. */
.dwg--mobile .dwg-toolbar ::v-deep .dtl__close { width: 26px; border-radius: 0; }
.dwg--mobile .dwg-toolbar ::v-deep .dtl__start { border-radius: 0; }
/* En el teléfono los dashboards guardados van en su fila, con scroll horizontal. */
.dwg--mobile .dpb { flex: 0 0 100%; }
</style>
